import {
  addEventListener,
  WixPatternsContainer,
  CollectionState,
  FiltersMap,
} from '@wix/bex-core';
import { action, reaction } from 'mobx';
import {
  DragAndDropParentState,
  DragAndDropState,
  DragItemOver,
} from './DragAndDropState';
import { CollectionDragAndDropBI } from './CollectionDragAndDropBI';

export type CollectionDragAndDropParentState<T, F extends FiltersMap> = Omit<
  DragAndDropParentState<T, F>,
  'collection'
> & {
  collection: CollectionState<T, F>;
  getCollectionSnapshot?: () => {
    [key: string]: string | number | undefined;
  };
};

interface CollectionDragAndDropStateParams<T, F extends FiltersMap> {
  container: WixPatternsContainer;
  state: CollectionDragAndDropParentState<T, F>;
  a11yContainer: HTMLElement | undefined | null;
}
export class CollectionDragAndDropState<T, F extends FiltersMap> {
  dnd: DragAndDropState<T, F>;
  state;
  bi;

  constructor(params: CollectionDragAndDropStateParams<T, F>) {
    this.state = params.state;
    this.bi = new CollectionDragAndDropBI({ state: params.state });

    this.dnd = new DragAndDropState<T, F>({
      state: this.state,
      bi: this.bi,
      collection: params.state.collection,
      container: params.container,
      a11yContainer: params.a11yContainer,
      move: this._collectionMove.bind(this),
      announcements: {
        dragHandleLabel: (id) => {
          const { translate: t } = params.container;
          const { itemName } = this.collection;

          const item = this.collection.getKeyedItem(id as string)?.item;

          return t('cairo.dragAndDrop.button-role-label.sr', {
            itemName: item ? itemName(item) : '',
          });
        },
        itemDescription: (id) => {
          const { itemName } = this.collection;

          const item = this.collection.getKeyedItem(id as string)?.item;

          return item ? itemName(item) : '';
        },
      },
    });
  }

  get collection() {
    return this.state.collection;
  }
  get optimisticActions() {
    return this.collection._optimisticActions;
  }

  init(params: { a11yContainer?: HTMLElement | null | undefined }) {
    const {
      optimisticActions,
      dnd: { container },
      bi,
    } = this;
    const { translate: t } = container;

    const disposers = [
      this.dnd.init(params),
      reaction(
        () => this.dnd.isDisabled,
        (isDisabled) => {
          if (!optimisticActions) {
            return;
          }

          optimisticActions._orderByMode = isDisabled ? 'orders' : 'moves';
        },
        {
          fireImmediately: true,
        },
      ),
      addEventListener(container.events, 'navigation', () => {
        if (optimisticActions?.hasRunningSequence) {
          return {
            title: t('cairo.dragAndDrop.leaveSite.modal.title'),
            content: t('cairo.dragAndDrop.leaveSite.modal.description'),
            onResume: action(() => {
              bi.onMoveFailure({
                reason: 'Left page',
                patches: optimisticActions.pendingMovePatches,
              });
            }),
          };
        }
        return null;
      }),
      ...(optimisticActions
        ? [
            addEventListener(
              optimisticActions.events,
              'dragAndDropSequenceError',
              ({ patches }) => {
                bi.onMoveFailure({
                  reason: 'Update failed',
                  patches,
                });
              },
            ),
            addEventListener(
              optimisticActions.events,
              'dragAndDropSequenceRetry',
              ({ patches }) => {
                bi.onMoveRetry({ patches });
              },
            ),
          ]
        : []),
    ];

    const onWindowClosedDuringSequence = () => {
      if (optimisticActions?.hasRunningSequence) {
        bi.onMoveFailure({
          reason: 'Window closed',
          patches: optimisticActions.pendingMovePatches,
        });
      }
    };

    window.addEventListener('unload', onWindowClosedDuringSequence);

    return action(() => {
      disposers.forEach((d) => d());
      window.removeEventListener('unload', onWindowClosedDuringSequence);
    });
  }

  _collectionMove(ev: { over: DragItemOver | null }) {
    const { onSubmit, reorderModeState } = this.dnd;
    const { optimisticActions } = this;
    if (optimisticActions == null) {
      return;
    }

    const dragEndEvent = this.dnd._createDragEndEvent(ev);

    if (dragEndEvent == null) {
      return;
    }

    optimisticActions.move(dragEndEvent, {
      onTryAgain: async () => {
        await reorderModeState?.onBulkSubmit();
      },
      submit: reorderModeState
        ? () => reorderModeState?.onSubmit
        : onSubmit
        ? (event) => onSubmit(this.dnd._createSimpleDragEndEvent(event))
        : undefined,
      collectionSnapshot: this.state.getCollectionSnapshot?.() ?? {},
    });
  }
}
